import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useApi } from "./useApi";
import { useSessionSetAtom } from "../useSessionSetAtom";
import { createReadGuard } from "./_helpers/readGuard";
import type {
    UnconfirmedCharges,
    ClearChargeRequest,
    ClearRenewalChargeResponse,
    ClearActivationChargeResponse,
    ClearReturnedPaymentFeeChargeResponse,
} from "@models/billing";
import {
    unconfirmedChargesAtom,
    isFetchingUnconfirmedChargesAtom,
    isClearingUnconfirmedChargeAtom,
} from "@/store/billingAtoms";

// Admin unconfirmed-charge worklist + clear endpoints (#377/#382, #469). All
// four use skipErrorNotification: the page renders its own inline ErrorState, and
// the clear dialog surfaces failures (notably the released-with-no-held-record
// 400) inline rather than as a toast.
const UNCONFIRMED_CHARGES_ENDPOINT = "/billing/unconfirmed-charges";

// Which worklist read may publish to its atoms (PR 369 review 1-3). Module-
// scope, like useProductApi's: the atoms are module-scope. Without it the
// page's first read, answering after the reload that follows a clear, would
// bring the cleared row back. Latest-only (`isLatest`).
const unconfirmedChargesReads = createReadGuard();

export const useBillingApi = () => {
    const { callApi } = useApi();
    // Session-bound: an answer landing after sign-out must not refill them.
    const setUnconfirmedCharges = useSessionSetAtom(unconfirmedChargesAtom);
    const setIsFetching = useSetAtom(isFetchingUnconfirmedChargesAtom);
    const setIsClearing = useSetAtom(isClearingUnconfirmedChargeAtom);

    const getUnconfirmedCharges =
        useCallback(async (): Promise<UnconfirmedCharges> => {
            const { isLatest } = unconfirmedChargesReads.begin();

            setIsFetching(true);

            try {
                const data = await callApi<UnconfirmedCharges>(
                    UNCONFIRMED_CHARGES_ENDPOINT,
                    { skipErrorNotification: true },
                );
                if (isLatest()) setUnconfirmedCharges(data);

                return data;
            } finally {
                if (isLatest()) setIsFetching(false);
            }
        }, [callApi, setUnconfirmedCharges, setIsFetching]);

    // PR 240 contract: the body is REQUIRED — { resolution: 1 } (Voided),
    // { resolution: 2 } (Released) or, #439, { resolution: 3, transactionId }
    // (Settled), numeric per the enums-as-int convention. Refusals (400/409)
    // are rendered inline in the dialog by the caller.
    const clearRenewalCharge = useCallback(
        async (
            organizationId: string,
            request: ClearChargeRequest,
        ): Promise<ClearRenewalChargeResponse> => {
            setIsClearing(true);

            try {
                return await callApi<ClearRenewalChargeResponse>(
                    `/organizations/${organizationId}/subscriptions/clear-unconfirmed-charge`,
                    {
                        method: "POST",
                        body: JSON.stringify(request),
                        skipErrorNotification: true,
                    },
                );
            } finally {
                setIsClearing(false);
            }
        },
        [callApi, setIsClearing],
    );

    const clearActivationCharge = useCallback(
        async (
            locationId: string,
            request: ClearChargeRequest,
        ): Promise<ClearActivationChargeResponse> => {
            setIsClearing(true);

            try {
                return await callApi<ClearActivationChargeResponse>(
                    `/locations/${locationId}/clear-unconfirmed-charge`,
                    {
                        method: "POST",
                        body: JSON.stringify(request),
                        skipErrorNotification: true,
                    },
                );
            } finally {
                setIsClearing(false);
            }
        },
        [callApi, setIsClearing],
    );

    // #469: an order's unconfirmed returned-payment fee charge. Same body,
    // codes and inline refusals as the two above; addressed by order id.
    const clearReturnedPaymentFeeCharge = useCallback(
        async (
            orderId: string,
            request: ClearChargeRequest,
        ): Promise<ClearReturnedPaymentFeeChargeResponse> => {
            setIsClearing(true);

            try {
                return await callApi<ClearReturnedPaymentFeeChargeResponse>(
                    `/orders/${orderId}/returned-payment-fee/clear-unconfirmed-charge`,
                    {
                        method: "POST",
                        body: JSON.stringify(request),
                        skipErrorNotification: true,
                    },
                );
            } finally {
                setIsClearing(false);
            }
        },
        [callApi, setIsClearing],
    );

    return {
        getUnconfirmedCharges,
        clearRenewalCharge,
        clearActivationCharge,
        clearReturnedPaymentFeeCharge,
    };
};
